'use client'

import { cn } from '@brew/ui/cn'
import { ArrowRight, Check, Mail, Send, Sparkles } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import Image from 'next/image'
import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { IntegrationBrandLogo } from '@/app/(app)/integrations/components/integration-brand-logo'
import { FanOutLinesIcon } from '@/components/marketing/icons/fan-out-lines-icon'
import type { IntegrationProvider } from '@/lib/data/convex/integrations'

/**
 * Minimal, animated tile visuals for the "Plus a lot more" bento. Each is a
 * compact, theme-aware illustration that replaces the dashed
 * `MediaPlaceholder` fallback. Wide (2-column) tiles compose across the full
 * tile width via `tall`; small tiles stay centered and compact. Motion is
 * purposeful (a single connected/sent highlight, a staggered reveal, a slow
 * crossfade) rather than ambient noise, and every visual respects
 * `prefers-reduced-motion`.
 */

/** Soft fade on the bottom edge so visuals dissolve into the tile instead of
 * hard-cutting (and any sub-pixel tile clip reads as intentional). */
const BOTTOM_FADE_MASK: CSSProperties = {
  maskImage: 'linear-gradient(to bottom, black calc(100% - 16px), transparent)',
  WebkitMaskImage:
    'linear-gradient(to bottom, black calc(100% - 16px), transparent)',
}

function VisualFrame({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'relative flex h-24 w-full items-center overflow-hidden',
        className
      )}
      style={BOTTOM_FADE_MASK}
    >
      {children}
    </div>
  )
}

function Chip({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-xl border border-border bg-background shadow-sm',
        className
      )}
    >
      {children}
    </span>
  )
}

/** Soft horizontal fade so a full-width rail reads as a window, not a strip. */
const EDGE_FADE_MASK: CSSProperties = {
  maskImage:
    'linear-gradient(to right, transparent, black 9%, black 91%, transparent)',
  WebkitMaskImage:
    'linear-gradient(to right, transparent, black 9%, black 91%, transparent)',
}

const RAIL_LOGOS: Array<IntegrationProvider> = [
  'stripe',
  'shopify',
  'figma',
  'clerk',
  'mailchimp',
  'slack',
  'hubspot',
  'klaviyo',
]

/**
 * Full-width logo rail (wide tile). A single "connected" check travels across
 * the chips one at a time, so the ecosystem reads as actively linking up
 * instead of every logo bobbing on the same sine wave.
 */
export function IntegrationsVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(
      () => setActive((index) => (index + 1) % RAIL_LOGOS.length),
      1100
    )
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  return (
    <VisualFrame>
      <div
        className="flex w-full items-center justify-between gap-1.5 md:gap-2"
        style={EDGE_FADE_MASK}
      >
        {RAIL_LOGOS.map((provider, index) => {
          const isActive = !shouldReduceMotion && index === active
          return (
            <div key={provider} className="relative shrink-0">
              <div
                className={cn(
                  'flex size-9 items-center justify-center rounded-xl border border-border bg-background shadow-sm transition-all duration-500 md:size-11',
                  { 'border-primary/60 shadow-md': isActive }
                )}
              >
                <IntegrationBrandLogo
                  provider={provider}
                  name={provider}
                  variant="card"
                  className="size-4 md:size-5"
                  imgClassName="size-4 md:size-5"
                />
              </div>
              <AnimatePresence>
                {isActive ? (
                  <motion.span
                    className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Check className="size-2.5" strokeWidth={3} />
                  </motion.span>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </VisualFrame>
  )
}

const BRAND_SWATCHES = [
  { color: '#ff6512', hex: 'FF6512' },
  { color: '#0ea5e9', hex: '0EA5E9' },
  { color: '#10b981', hex: '10B981' },
  { color: '#f59e0b', hex: 'F59E0B' },
  { color: '#1f2937', hex: '1F2937' },
]

/**
 * A mini brand sheet (wide tile): palette + type specimen + voice token laid
 * out like a real brand-kit card. Swatches reveal in a one-shot stagger on
 * scroll instead of looping forever.
 */
export function BrandAwareVisual() {
  const shouldReduceMotion = useReducedMotion()
  return (
    <VisualFrame>
      <div className="flex w-full items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 shadow-sm">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="font-medium text-[0.55rem] text-muted-foreground uppercase tracking-wide">
            Palette
          </span>
          <div className="flex items-end gap-1.5">
            {BRAND_SWATCHES.map((swatch, index) => (
              <motion.span
                key={swatch.hex}
                className="h-7 min-w-0 flex-1 rounded"
                style={{
                  backgroundColor: swatch.color,
                  transformOrigin: 'bottom',
                }}
                initial={shouldReduceMotion ? false : { scaleY: 0, opacity: 0 }}
                whileInView={
                  shouldReduceMotion ? undefined : { scaleY: 1, opacity: 1 }
                }
                viewport={{ once: true }}
                transition={{
                  delay: index * 0.07,
                  duration: 0.4,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              />
            ))}
          </div>
        </div>

        <div className="h-10 w-px shrink-0 bg-border" />

        <div className="flex shrink-0 flex-col gap-1">
          <span className="font-medium text-[0.55rem] text-muted-foreground uppercase tracking-wide">
            Type
          </span>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-2xl text-foreground leading-none">
              Aa
            </span>
            <div className="flex flex-col gap-1">
              <span className="h-1.5 w-12 rounded bg-foreground/15" />
              <span className="h-1.5 w-9 rounded bg-foreground/10" />
            </div>
          </div>
        </div>

        <div className="hidden flex-col gap-0.5 rounded-lg bg-muted/50 px-2.5 py-1.5 sm:flex">
          <span className="font-medium text-[0.55rem] text-muted-foreground uppercase tracking-wide">
            Voice
          </span>
          <span className="font-medium text-foreground text-xs">Confident</span>
        </div>
      </div>
    </VisualFrame>
  )
}

const GALLERY_SLOT_COUNT = 14
/** Card indices the selection ring hops between along the single row. */
const GALLERY_SELECTION_ORDER = [2, 6, 4, 8, 3]

/**
 * A pannable-canvas teaser (wide tile): a single row of real public template
 * thumbnails. Narrow, full-height cards so more of each email shows, on a
 * dotted canvas that pans slowly, with a selection ring that hops between
 * templates. Falls back to skeleton cards when no thumbnails load.
 */
export function TemplatesGalleryVisual({ images }: { images?: Array<string> }) {
  const shouldReduceMotion = useReducedMotion()
  const hasImages = Boolean(images && images.length > 0)
  const slots = Array.from({ length: GALLERY_SLOT_COUNT }, (_, index) =>
    hasImages && images ? images[index % images.length] : null
  )

  const [orderIndex, setOrderIndex] = useState(0)
  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(
      () =>
        setOrderIndex((value) => (value + 1) % GALLERY_SELECTION_ORDER.length),
      1600
    )
    return () => clearInterval(id)
  }, [shouldReduceMotion])
  const selected = GALLERY_SELECTION_ORDER[orderIndex]

  return (
    <VisualFrame>
      <div
        className="relative h-full w-full overflow-hidden rounded-xl border border-border bg-muted/30"
        style={{
          backgroundImage:
            'radial-gradient(circle, var(--border) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
          ...EDGE_FADE_MASK,
        }}
      >
        <motion.div
          className="absolute inset-0 flex items-stretch gap-2 p-3"
          animate={shouldReduceMotion ? undefined : { x: [0, -16, 0] }}
          transition={
            shouldReduceMotion
              ? undefined
              : {
                  duration: 14,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: 'easeInOut',
                }
          }
        >
          {slots.map((src, index) => {
            const isSelected = !shouldReduceMotion && index === selected
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: static decorative visual data — never reordered
                key={index}
                className={cn(
                  'relative h-full w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-all duration-500',
                  { 'z-10 border-primary ring-2 ring-primary/35': isSelected }
                )}
              >
                {src ? (
                  <Image
                    src={src}
                    alt=""
                    fill
                    sizes="60px"
                    quality={40}
                    className="object-cover object-top"
                  />
                ) : (
                  <div className="flex h-full flex-col gap-1 p-1.5">
                    <span className="h-2.5 rounded-sm bg-foreground/10" />
                    <span className="h-1 w-full rounded-sm bg-foreground/10" />
                    <span className="h-1 w-5/6 rounded-sm bg-foreground/10" />
                    <span className="mt-auto h-1 w-1/2 rounded-sm bg-primary/30" />
                  </div>
                )}
              </div>
            )
          })}
        </motion.div>
      </div>
    </VisualFrame>
  )
}

const SEGMENT_EXAMPLES = [
  {
    prompt: 'Active in the last 30 days who opened an email',
    chips: ['last_active < 30d', 'opened ≥ 1'],
    count: '4,182',
  },
  {
    prompt: 'US + EU customers who spent over $100',
    chips: ['region: US, EU', 'spent > $100'],
    count: '1,947',
  },
]

/**
 * Natural-language audience translation (wide tile): a plain-English phrase
 * resolves into structured filter chips plus a matched-contact count. The two
 * examples crossfade on a loop. No typewriter.
 */
export function SegmentsVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(
      () => setIndex((value) => (value + 1) % SEGMENT_EXAMPLES.length),
      3800
    )
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  const activeIndex = shouldReduceMotion ? 0 : index
  const example = SEGMENT_EXAMPLES[activeIndex]

  return (
    <VisualFrame>
      <div className="flex w-full items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5 shadow-sm">
          <Sparkles className="size-3.5 shrink-0 text-primary" />
          <AnimatePresence mode="wait">
            <motion.span
              key={activeIndex}
              className="min-w-0 flex-1 truncate text-foreground text-xs"
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {example.prompt}
            </motion.span>
          </AnimatePresence>
        </div>

        <ArrowRight className="size-4 shrink-0 text-muted-foreground" />

        <div className="flex shrink-0 flex-col gap-1.5 rounded-xl border border-border bg-background px-3 py-2.5 shadow-sm">
          <div className="flex flex-wrap items-center gap-1.5">
            <AnimatePresence mode="popLayout">
              {example.chips.map((chip, chipIndex) => (
                <motion.span
                  key={`${activeIndex}-${chip}`}
                  className="rounded-lg bg-primary/10 px-1.5 py-0.5 font-mono text-[0.6rem] text-primary"
                  initial={shouldReduceMotion ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: chipIndex * 0.1, duration: 0.25 }}
                >
                  {chip}
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
          <span className="text-[0.6rem] text-muted-foreground">
            <span className="font-semibold text-foreground">
              {example.count}
            </span>{' '}
            contacts
          </span>
        </div>
      </div>
    </VisualFrame>
  )
}

/**
 * Figma → email transform (small tile). A Figma frame crossfades into a
 * finished email card in place. A transform, deliberately distinct from the
 * branching transit in `SendAnywhereVisual`.
 */
export function FigmaToEmailVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [isEmailShown, setIsEmailShown] = useState(false)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(() => setIsEmailShown((value) => !value), 2200)
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  const isEmailVisible = shouldReduceMotion ? true : isEmailShown

  return (
    <VisualFrame className="justify-center">
      <div className="relative h-20 w-36">
        <motion.div
          className="absolute inset-0 flex flex-col gap-1.5 rounded-lg border border-border bg-background p-2.5 shadow-sm"
          animate={{ opacity: isEmailVisible ? 0 : 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-1.5">
            <IntegrationBrandLogo
              provider="figma"
              name="Figma"
              variant="card"
              className="size-4"
              imgClassName="size-4"
            />
            <span className="h-1.5 w-12 rounded bg-foreground/10" />
          </div>
          <div className="mt-1 flex-1 rounded border border-primary/40 border-dashed" />
        </motion.div>

        <motion.div
          className="absolute inset-0 flex flex-col gap-1.5 rounded-lg border border-border bg-background p-2.5 shadow-sm"
          animate={{ opacity: isEmailVisible ? 1 : 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-1.5 rounded bg-primary/15 px-1.5 py-1">
            <Mail className="size-3 text-primary" />
            <span className="h-1.5 w-10 rounded bg-primary/30" />
          </div>
          <span className="h-1.5 w-full rounded bg-foreground/10" />
          <span className="h-1.5 w-2/3 rounded bg-foreground/10" />
          <span className="mt-auto h-2 w-12 rounded bg-primary/30" />
        </motion.div>
      </div>
    </VisualFrame>
  )
}

const HANDLE_POSITIONS = [
  '-top-1 -left-1',
  '-top-1 -right-1',
  '-bottom-1 -left-1',
  '-bottom-1 -right-1',
]

/**
 * Inline prompt editing (small tile). A selected text block (corner handles) +
 * a prompt chip, where one line grows from faint/short to solid/full. A
 * single intentional before→after, not an infinite border pulse.
 */
export function EditWithPromptsVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [hasEdited, setHasEdited] = useState(false)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(() => setHasEdited((value) => !value), 2400)
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  const isEdited = shouldReduceMotion ? true : hasEdited

  return (
    <VisualFrame className="justify-center">
      <div className="relative w-44">
        <div className="relative flex flex-col gap-1.5 rounded-lg border border-primary/50 bg-background p-3 shadow-sm">
          <span className="h-2 w-3/4 rounded bg-foreground/10" />
          <motion.span
            className="rounded bg-primary"
            style={{ height: '0.5rem' }}
            animate={{
              width: isEdited ? '100%' : '45%',
              opacity: isEdited ? 1 : 0.35,
            }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
          />
          <span className="h-2 w-1/2 rounded bg-foreground/10" />
          {HANDLE_POSITIONS.map((position) => (
            <span
              key={position}
              aria-hidden="true"
              className={cn(
                'absolute size-2 rounded-full border border-primary bg-background',
                position
              )}
            />
          ))}
        </div>
        <span className="absolute right-2 -bottom-2 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 font-medium text-[0.6rem] text-primary-foreground shadow-sm">
          <Sparkles className="size-3" />
          make it bolder
        </span>
      </div>
    </VisualFrame>
  )
}

const ESP_DESTINATIONS: Array<IntegrationProvider> = [
  'klaviyo',
  'mailchimp',
  'sendgrid',
]
/** Vertical center of each ESP chip within the shared 80-unit column, so the
 * SVG connectors land exactly on the chip centers. */
const ESP_ANCHOR_Y = [12, 40, 68]

/**
 * Send anywhere (small tile). One source branches out to several ESP
 * destinations; a "sent" check lights each one in turn. Branching transit,
 * deliberately distinct from the Figma transform.
 */
export function SendAnywhereVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(
      () => setActive((index) => (index + 1) % ESP_DESTINATIONS.length),
      850
    )
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  return (
    <VisualFrame className="justify-center gap-1">
      <Chip className="size-11 shrink-0 border-transparent bg-primary text-primary-foreground">
        <Send className="size-5" />
      </Chip>
      <FanOutLinesIcon
        anchorsY={ESP_ANCHOR_Y}
        activeIndex={shouldReduceMotion ? null : active}
        className="h-20 w-10 shrink-0"
      />
      <div className="flex h-20 shrink-0 flex-col justify-between">
        {ESP_DESTINATIONS.map((provider, index) => {
          const isActive = !shouldReduceMotion && index === active
          return (
            <div key={provider} className="relative">
              <Chip
                className={cn('size-6 transition-colors duration-300', {
                  'border-primary/60': isActive,
                })}
              >
                <IntegrationBrandLogo
                  provider={provider}
                  name={provider}
                  variant="card"
                  className="size-3.5"
                  imgClassName="size-3.5"
                />
              </Chip>
              <AnimatePresence>
                {isActive ? (
                  <motion.span
                    className="absolute -top-1 -right-1 flex size-3 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                  >
                    <Check className="size-2" strokeWidth={3} />
                  </motion.span>
                ) : null}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </VisualFrame>
  )
}

const API_MCP_EVENTS = ['delivered', 'opened', 'clicked']
const API_MCP_CYCLE = API_MCP_EVENTS.length + 2

/** The two ways into Brew, crossfaded in the panel header: a REST request and
 * an MCP tool call. Both resolve to the same realtime event stream. */
const API_MCP_SOURCES = [
  { kind: 'POST', target: '/v1/emails', status: '201' },
  { kind: 'MCP', target: 'brew · send_email', status: 'ok' },
] as const

/**
 * API + MCP (small tile). The panel header alternates between a REST `POST`
 * and an MCP tool call — the two entry points into Brew — while the same
 * realtime event rows (`delivered`, `opened`, `clicked`) light up in sequence
 * below. Says "two ways in, one engine" rather than pulsing equalizer bars.
 */
export function ApiMcpVisual() {
  const shouldReduceMotion = useReducedMotion()
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (shouldReduceMotion) {
      return
    }
    const id = setInterval(() => setTick((value) => value + 1), 800)
    return () => clearInterval(id)
  }, [shouldReduceMotion])

  const visible = shouldReduceMotion
    ? API_MCP_EVENTS.length
    : Math.min(tick % API_MCP_CYCLE, API_MCP_EVENTS.length)
  const sourceIndex = shouldReduceMotion
    ? 1
    : Math.floor(tick / API_MCP_CYCLE) % API_MCP_SOURCES.length
  const source = API_MCP_SOURCES[sourceIndex]

  return (
    <VisualFrame className="justify-center">
      <div className="w-full max-w-[16rem] rounded-lg border border-border bg-background font-mono shadow-sm">
        <div className="flex items-center gap-2 border-border border-b px-3 py-2 text-[0.7rem]">
          <AnimatePresence mode="wait">
            <motion.div
              key={sourceIndex}
              className="flex min-w-0 flex-1 items-center gap-2"
              initial={shouldReduceMotion ? false : { opacity: 0, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3 }}
              transition={{ duration: 0.22 }}
            >
              <span className="font-semibold text-primary">{source.kind}</span>
              <span className="truncate text-muted-foreground">
                {source.target}
              </span>
              <span className="ml-auto rounded bg-green-500/15 px-1.5 py-0.5 font-semibold text-[0.6rem] text-green-600 dark:text-green-500">
                {source.status}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex flex-col gap-1 px-3 py-2">
          {API_MCP_EVENTS.map((event, index) => {
            const isShown = index < visible
            return (
              <div
                key={event}
                className={cn(
                  'flex items-center gap-2 text-[0.6rem] transition-opacity duration-300',
                  isShown ? 'opacity-100' : 'opacity-25'
                )}
              >
                <span
                  className={cn('size-1.5 rounded-full transition-colors', {
                    'bg-green-500': isShown,
                    'bg-border': !isShown,
                  })}
                />
                <span className="text-muted-foreground">event</span>
                <span className="text-foreground">{event}</span>
              </div>
            )
          })}
        </div>
      </div>
    </VisualFrame>
  )
}
